/**
 * The broadcast composer's hero image: pick a file, presign it, PUT the bytes,
 * hand back the pair the draft needs (`dataUrl` + `blobPath`).
 *
 * Hand-rolled useState/useCallback, matching `useBroadcastEstimate.js` and the
 * rest of the comms surface, including the generation-counter idiom that stops
 * a slow upload from overwriting a faster newer one.
 *
 * ---------------------------------------------------------------------------
 * THE RULE THIS HOOK EXISTS TO PROTECT
 * ---------------------------------------------------------------------------
 * AN UNRECOGNISED UPLOAD SCOPE MUST NOT TAKE THE COMPOSER DOWN.
 *
 * `presignBroadcastImage` sends `scope: "broadcast"`. That scope is an
 * ASSUMPTION about v1_10_0 — see the note on the service function; it has never
 * been confirmed against a running backend. If this build of ms-agents does not
 * know it, the presign answers 4xx, and that 4xx means "no such scope here",
 * not "your file is bad" and not "try again in a moment".
 *
 * So that branch sets `unsupported: true` and a plain-language `error`, and it
 * DOES NOT THROW. The image is one optional field on a form that also carries a
 * title, a body, a destination, an audience and a send button; an unverified
 * assumption about one field must not be able to break the other five. This
 * branch is the entire reason the assumption is safe to ship — remove it and a
 * backend that never heard of the scope turns every broadcast into an error
 * screen.
 *
 * A 5xx or a network failure is deliberately NOT the same thing: it is an
 * ordinary transient `error` with `unsupported` left false. One says "this will
 * never work here, stop offering it"; the other says "try again". Collapsing
 * them would either hide a real outage behind a permanent-sounding message or
 * invite the author to retry something that can never succeed.
 *
 * ---------------------------------------------------------------------------
 * VALIDATE BEFORE YOU ASK
 * ---------------------------------------------------------------------------
 * Type and size are checked before any network call. Both are facts we already
 * hold locally, and a presign round-trip to be told what a `File` object could
 * have told us instantly is latency the author pays for nothing.
 *
 * Dimensions are the opposite: advisory only. A hero that is not 1200×628 still
 * sends — it crops — so a mismatch sets `warning` and the upload CONTINUES. A
 * probe that could not measure the file says nothing at all rather than
 * guessing.
 *
 * ---------------------------------------------------------------------------
 * NEVER A HALF-ATTACHED IMAGE
 * ---------------------------------------------------------------------------
 * `dataUrl` and `blobPath` are set together, only after the PUT comes back ok.
 * A presigned URL exists the moment presign returns, but it points at a blob
 * with no bytes in it — attaching that to a draft ships a broadcast whose hero
 * image is a 404 to every recipient. Any failure leaves both empty.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { post as commsPost } from "../../../service/comms/commsApi";
import { presignBroadcastImage } from "../../../service/comms/broadcastsService";
import { probeImageSize } from "../../../utils/imageProbe";
import { compressImage } from "../../../utils/imageCompress";
import {
  IMAGE_HEIGHT,
  IMAGE_MAX_BYTES,
  IMAGE_MIME_TYPES,
  IMAGE_WIDTH,
} from "./broadcastEnums";
import { messageFor } from "./broadcastErrors";

const BYTES_PER_MB = 1024 * 1024;

/**
 * Copy for the graceful-degradation branch.
 *
 * `messageFor` is not used here on purpose. Its 422 line is "check the
 * highlighted fields", which sends the author hunting through a form where
 * nothing is wrong — the server simply does not host this upload scope.
 * Everywhere else in this hook the error copy does come from `messageFor`.
 */
const UNSUPPORTED_MESSAGE =
  "Images aren't available on this server yet. Everything else in this broadcast still sends.";

/** Copy for a PUT that answered non-ok. No status to read, so no `messageFor`. */
const PUT_FAILED_MESSAGE = "That image could not be uploaded. Please try again.";

/** Bytes as a short human string, e.g. `2.4 MB`. */
const asMb = (bytes) => `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;

/** The accepted types, spelled the way a person reads them: "JPG or PNG". */
const acceptedLabel = () =>
  IMAGE_MIME_TYPES.map((mime) => mime.replace("image/", "").replace("jpeg", "jpg").toUpperCase())
    .join(" or ");

const typeMessage = (file) =>
  `That file is ${file?.type || "an unknown type"}. Broadcast images must be ${acceptedLabel()}.`;

const sizeMessage = (file) =>
  `That image is ${asMb(file.size)}. The limit is ${asMb(IMAGE_MAX_BYTES)}.`;

/**
 * Advisory, shown after a successful downscale. Says what happened and to what,
 * because an image the author picked has silently changed and they should know
 * — a PNG becomes a JPEG, and any transparency is now white.
 */
const resizedMessage = (originalSize, finalSize) =>
  `That image was ${asMb(originalSize)}, so it was resized to ${asMb(finalSize)} ` +
  `to fit the ${asMb(IMAGE_MAX_BYTES)} limit.`;

const dimensionWarning = (width, height) =>
  `That image is ${width}×${height}. It will be cropped or letterboxed; ` +
  `${IMAGE_WIDTH}×${IMAGE_HEIGHT} looks best.`;

/**
 * Is this rejection the backend saying it does not know the `"broadcast"`
 * scope? Any 4xx is: the request was well-formed and the bytes were never sent,
 * so the only thing left for the server to object to is the scope itself.
 *
 * @param {{ status?: number }} err
 * @returns {boolean}
 */
const isScopeRejection = (err) => {
  const status = Number(err?.status);
  return Number.isFinite(status) && status >= 400 && status < 500;
};

/**
 * Own the broadcast composer's optional hero image.
 *
 * @param {Object} [options]
 * @param {(media: { dataUrl: string, blobPath: string }) => void} [options.onUploaded]
 *   Called once the bytes are actually in storage, so the composer can put the
 *   pair on its draft.
 * @param {() => void} [options.onCleared] - Called by `clear()`.
 * @returns {{
 *   pick: (file: File) => Promise<void>,
 *   clear: () => void,
 *   uploading: boolean,
 *   error: string|null,
 *   warning: string|null,
 *   unsupported: boolean,
 *   dataUrl: string,
 *   blobPath: string,
 *   fileName: string,
 * }}
 */
export default function useBroadcastImage({ onUploaded, onCleared } = {}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [unsupported, setUnsupported] = useState(false);
  const [dataUrl, setDataUrl] = useState("");
  const [blobPath, setBlobPath] = useState("");
  const [fileName, setFileName] = useState("");
  /**
   * A local `blob:` URL for the file the author just picked.
   *
   * WHY THIS EXISTS: the composer previews an image it already holds in
   * memory, so asking the server to authorise a signed URL for it is a round
   * trip that can only make things worse. It did: `GET /uploads/media-url`
   * answers 404 for a broadcast image, because that endpoint authorises by
   * walking the room message carrying the blob — and a broadcast image is
   * never attached to a message. The upload had SUCCEEDED; only the preview
   * failed, which is the most confusing possible way for this to break.
   *
   * The signed-URL path still matters for an image restored from a saved
   * record, where there is no local file. `ImageDropzone` prefers this and
   * falls back to that.
   */
  const [previewUrl, setPreviewUrl] = useState("");

  /**
   * The generation of the most recently STARTED attempt. Bumped synchronously
   * inside `pick` (and by `clear`), before the first await, so a second pick
   * invalidates the first the instant it is called rather than whenever its
   * first promise happens to settle.
   */
  const generationRef = useRef(0);
  /** The object URL currently held, so it can be revoked before replacing. */
  const previewUrlRef = useRef("");

  /**
   * Replace the local preview, revoking whatever it displaced.
   *
   * An object URL pins its blob in memory until revoked, so picking five
   * images in a row without this leaks all five.
   */
  const setLocalPreview = useCallback((file) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const next = file ? URL.createObjectURL(file) : "";
    previewUrlRef.current = next;
    setPreviewUrl(next);
  }, []);

  // Unmount invalidates whatever is in flight, matching `useBroadcastList`,
  // `useBroadcastDetail`, `useBroadcastEstimate` and `useBroadcastRecipients`.
  //
  // Without it, navigating away mid-upload lets the presign/PUT chain resolve
  // into a dead tree: the setters are harmless, but `onUploaded` reaches back
  // into the composer's `setDraft`, so an unmounted composer would still be
  // mutating a draft nobody is editing any more.
  useEffect(
    () => () => {
      generationRef.current += 1;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    },
    []
  );

  const clear = useCallback(() => {
    // Bump first: an upload still in flight is now answering a question nobody
    // asked, and must not be allowed to re-attach itself after this.
    generationRef.current += 1;
    setUploading(false);
    setError(null);
    setWarning(null);
    setUnsupported(false);
    setDataUrl("");
    setBlobPath("");
    setFileName("");
    setLocalPreview(null); // revokes the object URL rather than orphaning it
    onCleared?.();
  }, [onCleared, setLocalPreview]);

  const pick = useCallback(
    async (file) => {
      if (!file) return;

      // Bump FIRST — before the local checks, which can return early.
      //
      // The bug this ordering fixes: pick a valid image A (upload starts),
      // then drop an invalid file B while A is still in flight. B fails the
      // type check and returns. If the bump lived below that check, B would
      // never have invalidated A, so A's upload would later resolve and
      // silently attach itself to the draft while B's rejection message was
      // still on screen — an attached image the author never sees, sitting
      // behind an error about a different file.
      //
      // Picking ANY file, valid or not, is the author saying "not that one".
      // Invalidating whatever is in flight is what that means.
      const generation = generationRef.current + 1;
      generationRef.current = generation;
      /** A response is worth acting on only while it is still the current one. */
      const isStale = () => generationRef.current !== generation;

      setError(null);
      setWarning(null);
      setUnsupported(false);

      /* ── Local checks. No network call may happen above this line. ── */
      if (!IMAGE_MIME_TYPES.includes(file.type)) {
        setError(typeMessage(file));
        setUploading(false);
        return;
      }
      // NOTE: size is deliberately NOT a rejection. An oversized image is
      // downscaled to fit below, because a 6 MB phone photo is a solvable
      // problem and "that file is too big" makes it the author's problem.

      // The previous image is gone the moment a replacement is accepted — a
      // draft must never show the old hero while a new one is uploading.
      setDataUrl("");
      setBlobPath("");
      setFileName("");
      setUploading(true);
      // Immediately, from the file itself. The author should see their image
      // the moment they drop it, not after a presign and a PUT.
      setLocalPreview(file);

      let upload = file;
      try {
        const result = await compressImage(file, IMAGE_MAX_BYTES);
        if (isStale()) return;
        upload = result.file;
        if (result.compressed) {
          // Informational, not a failure — the upload proceeds. Rendered in
          // the advisory (non-alert) slot alongside the dimension warning.
          setWarning(resizedMessage(result.originalSize, upload.size));
        }
      } catch (err) {
        // Only reachable when the image cannot be read at all, or cannot be
        // brought under the limit even at the smallest bound and lowest
        // quality. Both are genuine dead ends, unlike mere size.
        if (isStale()) return;
        setError(err?.message || sizeMessage(file));
        setUploading(false);
        return;
      }

      try {
        // Advisory only: a mismatch warns, a failure to measure says nothing,
        // and neither stops what follows.
        const { width, height } = await probeImageSize(file);
        if (isStale()) return;
        if (
          width != null &&
          height != null &&
          (width !== IMAGE_WIDTH || height !== IMAGE_HEIGHT)
        ) {
          setWarning(dimensionWarning(width, height));
        }

        let response;
        try {
          response = await presignBroadcastImage(commsPost, {
            fileName: upload.name,
            contentType: upload.type,
            fileSize: upload.size,
          });
        } catch (err) {
          if (isStale()) return;
          // ── The graceful-degradation branch. See the header. ──
          // Handled, not rethrown: the composer stays usable and sendable.
          if (isScopeRejection(err)) {
            setUnsupported(true);
            setError(UNSUPPORTED_MESSAGE);
            return;
          }
          // 5xx / network: transient, `unsupported` stays false, retry is real.
          throw err;
        }
        if (isStale()) return;

        const media = response?.data || {};
        // Raw bytes straight to storage, exactly as Messages/Composer.jsx does.
        // The blob-type header is not optional: Azure rejects a PUT without it.
        const putRes = await fetch(media.upload_url, {
          method: "PUT",
          headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": upload.type },
          body: upload,
        });
        if (isStale()) return;
        if (!putRes?.ok) throw new Error(PUT_FAILED_MESSAGE);

        const nextDataUrl = media.data_url || "";
        const nextBlobPath = media.blob_path || "";
        setDataUrl(nextDataUrl);
        setBlobPath(nextBlobPath);
        setFileName(upload.name);
        // Re-point at the bytes actually uploaded: a compressed image is a
        // different file, and previewing the original would show a quality
        // the recipient will not get.
        if (upload !== file) setLocalPreview(upload);
        onUploaded?.({ dataUrl: nextDataUrl, blobPath: nextBlobPath });
      } catch (err) {
        if (isStale()) return;
        setError(messageFor(err));
        // Belt and braces on the half-attached rule: nothing partial survives
        // a failure, whichever step threw.
        setDataUrl("");
        setBlobPath("");
        setFileName("");
      } finally {
        // A superseded attempt owns none of the UI any more, including the
        // spinner the newer attempt is currently using.
        if (!isStale()) setUploading(false);
      }
    },
    [onUploaded]
  );

  return {
    pick,
    clear,
    uploading,
    error,
    warning,
    unsupported,
    dataUrl,
    previewUrl,
    blobPath,
    fileName,
  };
}
